import { Popover, Tag, Tooltip, Tree } from 'antd';
import moment from 'moment';
import { FilterFilled, InfoCircleOutlined, PlayCircleTwoTone } from '@ant-design/icons';
import envConfig from '@hulu/env-config';
import {
  createTableColumnFilters,
  createTableRowToolTip,
  auctionSnapshotActions,
  editableSnapshotNameCell,
  snapshotStatuses,
  StyledAuctionName,
  createTableFiltersFromSubObjects,
} from './tableUtils';
import {
  ACTIVE,
  CHANGE_TYPE,
  CHANNEL_ID,
  DURATION_MS,
  EXPIRED,
  SERIES_ID,
  STATUS,
  LEAGUE_ID,
  SUSPENDED,
  TAG_VALID_STATUS,
  USER_PROFILE,
  FUBO_USER,
  SWID,
  VIEWER_ID,
  SINGLE_PROGRAM,
  AUCTION_SNAPSHOT_DETAILED_VIEW,
  PARTIAL_POD,
  SPORTS_ID,
  EVENT_ID,
  REQUIREMENT_VAST_PASS_STATUS,
  REQUIREMENT_MEDIA_PASS_STATUS,
  ADS_TRACER_ZIPKIN_LINK,
  ASSET_REVIEWER_LINK,
  VIEW,
  DEVICE,
  BACK_TO_BACK_ADS,
} from '../../constants';
import { buildZipkinTraceUrl } from '../../zipkinTrace';
import { dateRender, formatDate, getLocalTimezoneAbbreviation } from '../../utils';
import { idRender, ruleStatusRender } from '../DurationManager/utils';
import {
  RULE_CHANNEL_ID,
  RULE_DURATIONS_MILLISECONDS,
  RULE_LAST_UPDATED,
  RULE_SERIES_ID,
  RULE_LEAGUE_ID,
  RULE_SINGLE_PROGRAM,
  RULE_SINGLE_PROGRAM_DISPLAY,
  RULE_STATUS,
  RULE_TYPE,
  IDS,
  STREAM_TYPE,
  RULE_PARTIAL_POD,
  RULE_PARTIAL_POD_DISPLAY,
  RULE_EVENT_ID,
  RULE_SPORTS_ID,
  DURATION_APP,
  RULE_BACKTOBACKADS_ENABLED,
  RULE_BACKTOBACKADS,
} from '../DurationManager/constants';

import {
  ADOMAIN_KEY,
  ASSET_NAME_KEY,
  ASSET_ID_KEY,
  ASSET_SOURCES_KEY,
  BRAND_KEY,
  BRAND_NAME_KEY,
  CREATED_AT_KEY,
  UPDATED_AT_KEY,
  PREVIEW_KEY,
  REVIEW_STATUS_KEY,
  TRANSCODE_QC_PLAYLIST_KEY,
  SOURCE_AD_ID_KEY,
  SOURCE_CREATIVE_ID_KEY,
} from '../CreativeTrackerV2/constants';

import {
  AD,
  AD_ID,
  CREATIVE,
  ERROR_MESSAGE,
  IMPRESSION,
  INLINE,
  LINEAR,
  MEDIA_FILE,
  PARAMETER,
  INGESTION_NOTE,
  PRIMARY,
  TYPE,
  VALUE,
  VAST_PASS,
  VAST,
  VIDEO_BIT_RATE,
  VIDEO_RESOLUTION,
  NO_VALID_MEDIA_FILE,
  MEDIA_PASS_STATUS,
} from '../QCheck/constants';
import {
  AD_SERVER_TITLE,
  CONTENT_INFO_TITLE,
  CONTENT_DISTRIBUTOR_TITLE,
  COUNTRY_TITLE,
  DEVICE_TITLE,
  DEVICE_DISTRIBUTOR_TITLE,
  INTEGRATION_TITLE,
  POD_TITLE,
  PUBLISHER_TITLE,
  STATUS_TITLE,
  TEST_CASE_CREATED_AT,
  TEST_CASE_ID,
  TEST_CASE_PROFILE_NAME,
  TEST_CASE_STATUS,
  TIME_TITLE,
  TRACE_AD_SERVER,
  TRACE_CONTENT_PARTNER_NAME,
  TRACE_CONTENT_PARTNER_IDENTIFIER,
  TRACE_CONTENT_TITLE,
  TRACE_COUNTRY,
  TRACE_DEVICE_NAME,
  TRACE_DEVICE_DISTRIBUTOR_NAME,
  TRACE_CONTENT_DISTRIBUTOR_NAME,
  TRACE_INTEGRATION,
  TRACE_POD,
  TRACE_PUBLISHER,
  TRACE_SERIES_NAME,
  TRACE_SERIES_ID,
  TRACE_STATUS,
  TRACE_TIME,
  TRACE_ZIPKIN_TRACE_ID,
  TEST_CASE_ACTION,
  TRACE_ID_COLUMN_TITLE,
} from '../AdsTracer/constants';
import {
  configStatusRender,
  CONTENT_PARTNER_NAME,
  HISTORY_ACTION,
  NETWORK_NAME,
  POD_FETCH_METHOD,
  SOURCE_ID,
  TRIGGER_TYPE,
  UPDATED_AT,
  UPDATED_BY,
  UPID_LOOKUP_STRATEGY,
} from '../ConfigManager/utils';
import { USER_EMAIL, USER_ROLES } from '../PermissionManager/constants';
import {
  INVARIANT_QUALIFICATION_ENDPOINT,
  INVARIANT_QUALIFICATION_QUALIFIED_ADS,
  INVARIANT_QUALIFICATION_DQ_STATUS,
  INVARIANT_QUALIFICATION_DQ_REASON,
  SLOT_QUALIFICATION_SELECTION,
  SLOT_QUALIFICATION_ENDPOINT,
  SLOT_QUALIFICATION_INVARIANT_QUALIFIED,
  SLOT_QUALIFICATION_SLOT_QUALIFIED,
  SLOT_QUALIFICATION_DQ_STATUS,
  SLOT_QUALIFICATION_POD_POSITION,
  SLOT_QUALIFICATION_SLOT_POSITION,
  SLOT_QUALIFICATION_QUALIFIED_ADS,
  SLOT_QUALIFICATION_FILLER_ONLY,
  SLOT_QUALIFICATION_DQ_REASON,
  SLOT_QUALIFICATION_IN_NEXT_SEQUENCE,
} from '../RequestTracer/constants';
import BooleanBubble from '../RequestTracer/BooleanBubble';
import { getDQStatusBubble } from '../RequestTracer/styles';

export const STATUS_STRINGS = {
  CREATED: 'CREATED',
  FILLED: 'FILLED',
  SUCCESS: 'SUCCESS',
  POD_MISS: 'POD MISS',
  EMPTY: 'EMPTY',
  BAD_REQUEST: 'BAD REQUEST',
  INTERNAL_ERROR: 'INTERNAL ERROR',
  TIMEOUT: 'TIMEOUT',
};

export const STATUS_CODES = {
  OK: '200',
  CREATED: '201',
  NO_CONTENT: '204',
  BAD_REQUEST: '400',
  INTERNAL_ERROR: '500',
  TIMEOUT: '504',
};

// TEST CASES SUMMARIES TABLE
const testCaseTableStatusRender = (status, { isSharedToOther, isSharedToMe }) => {
  let testCaseStatus;
  let sharedToOtherStatus;
  let sharedToMeStatus;

  switch (status) {
    case ACTIVE:
      testCaseStatus = <Tag color="green">Active</Tag>;
      break;
    case SUSPENDED:
      testCaseStatus = <Tag color="orange">Suspended</Tag>;
      break;
    case EXPIRED:
      testCaseStatus = <Tag color="red">Expired</Tag>;
      break;
    default:
      testCaseStatus = <Tag color="blue">Unknown</Tag>;
  }

  if (isSharedToOther) {
    sharedToOtherStatus = <Tag color="yellow">Shared</Tag>;
  }

  if (isSharedToMe) {
    sharedToMeStatus = <Tag color="yellow">Shared to me</Tag>;
  }

  return (
    <>
      {testCaseStatus}
      {sharedToOtherStatus}
      {sharedToMeStatus}
    </>
  );
};

const getAccountIdDisplay = (publisher, accountId, profileId) => {
  if (accountId) {
    return publisher === 'ESPN' && !profileId ? [`Standard Web Id (SWID): ${accountId}`] : [`Account Id: ${accountId}`];
  }
  return [];
};

const testCaseTableRowRender = (
  val,
  {
    email,
    accountId,
    profileId,
    profileName,
    testCaseId,
    expireAt,
    createdAt,
    subscription,
    isSharedToOther,
    sharedTime,
    publisher,
    channel,
    distroPartner,
    ipAddress,
    deviceIfa,
    fuboUserId,
  }
) => {
  const localTimeZone = getLocalTimezoneAbbreviation();
  return createTableRowToolTip(val, [
    `Test Case Id: ${testCaseId}`,
    ...(email ? [`Email: ${email}`] : []),
    ...getAccountIdDisplay(publisher, accountId, profileId),
    ...(profileId ? [`Profile Id: ${profileId}`] : []),
    ...(profileName ? [`Profile Name: ${profileName}`] : []),
    ...(deviceIfa ? [`Device IFA: ${deviceIfa}`] : []),
    ...(ipAddress ? [`IP Address: ${ipAddress}`] : []),
    ...(channel ? [`Channel: ${channel}`] : []),
    ...(distroPartner ? [`Distribution Partner: ${distroPartner}`] : []),
    ...(fuboUserId ? [`Fubo User Id: ${fuboUserId}`] : []),
    `Created At (${localTimeZone}): ${createdAt}`,
    `Expired At (${localTimeZone}): ${expireAt}`,
    ...(subscription ? [`Subscriptions: ${subscription?.packages?.map((pkg) => pkg.value).join(' | ')}`] : []),
    ...(isSharedToOther ? ['*This test case is being shared with others'] : []),
    ...(sharedTime ? [`Test Case shared At (${localTimeZone}): ${sharedTime}`] : []),
  ]);
};

export const testCaseColumns = (testCases, rowExtraRender, customFilter, rangeFilter, widths) => [
  {
    title: 'Profile',
    dataIndex: TEST_CASE_PROFILE_NAME,
    key: TEST_CASE_PROFILE_NAME,
    ellipsis: true,
    render: testCaseTableRowRender,
    filters: createTableColumnFilters(testCases, TEST_CASE_PROFILE_NAME),
    filterSearch: true,
    onFilter: (value, record) => record[TEST_CASE_PROFILE_NAME] === value,
    width: widths[TEST_CASE_PROFILE_NAME] || 100,
  },
  {
    title: 'Test Case',
    dataIndex: TEST_CASE_ID,
    key: TEST_CASE_ID,
    ellipsis: true,
    render: testCaseTableRowRender,
    filterSearch: true,
    filters: createTableColumnFilters(testCases, TEST_CASE_ID),
    onFilter: (value, record) => record[TEST_CASE_ID] === value,
    width: widths[TEST_CASE_ID] || 100,
  },
  {
    title: 'Status',
    dataIndex: TEST_CASE_STATUS,
    key: TEST_CASE_STATUS,
    render: testCaseTableStatusRender,
    filters: createTableColumnFilters(testCases, TEST_CASE_STATUS),
    onFilter: (value, record) => record[TEST_CASE_STATUS] === value,
    width: widths[TEST_CASE_STATUS] || 100,
  },
  {
    title: `Time Created (${getLocalTimezoneAbbreviation()})`,
    dataIndex: TEST_CASE_CREATED_AT,
    key: TEST_CASE_CREATED_AT,
    render: (createdAt) => createdAt,
    width: widths[TEST_CASE_CREATED_AT] || 100,
    filterIcon: <FilterFilled style={{ color: rangeFilter ? '#1890ff' : undefined }} />,
    filterDropdown: customFilter,
  },
  {
    title: 'Actions',
    dataIndex: TEST_CASE_ACTION,
    key: TEST_CASE_ACTION,
    width: widths[TEST_CASE_ACTION] || 100,
    render: rowExtraRender,
  },
];

// TRACE SUMMARIES TABLE
const traceTableRowRender = (
  val,
  {
    trace_id,
    integration,
    contentPartnerName,
    contentPartnerIdentifier,
    seriesName,
    seriesIdentifier,
    contentTitle,
    deviceId,
    distributorPlatform,
    distributorName,
    pod,
    isVppa,
    isOptOut,
    isCoppa,
  }
) => {
  // Prioritize name over identifier fields
  const displayContentPartner = contentPartnerName !== 'N/A' ? contentPartnerName : contentPartnerIdentifier;
  const displaySeries = seriesName !== 'N/A' ? seriesName : seriesIdentifier;

  return createTableRowToolTip(val, [
    'Trace Id: ' + trace_id,
    'Integration: ' + integration,
    'Content Partner: ' + displayContentPartner,
    'Series Name: ' + displaySeries,
    'Content Title: ' + contentTitle,
    'Device Id: ' + deviceId,
    'Distributor Platform: ' + distributorPlatform,
    'Distributor Name: ' + distributorName,
    'Ad Position: ' + pod,
    'Vppa: ' + isVppa?.toString(),
    'Opt Out: ' + isOptOut?.toString(),
    'Coppa: ' + isCoppa?.toString(),
  ]);
};

const translateStatus = (status, pod) => {
  const isKnownStatus = (action) => pod && pod.includes(action);

  switch (status) {
    case STATUS_CODES.OK:
      if (isKnownStatus('CREATE')) return STATUS_STRINGS.CREATED;
      if (['ROLLOVER', 'BUILD'].some(isKnownStatus)) return STATUS_STRINGS.SUCCESS;
      return STATUS_STRINGS.FILLED; // GET pod call
    case STATUS_CODES.NO_CONTENT:
      if (isKnownStatus('BUILD')) return STATUS_STRINGS.EMPTY;
      return STATUS_STRINGS.POD_MISS; // GET pod call
    case STATUS_CODES.CREATED:
      return STATUS_STRINGS.SUCCESS;
    case STATUS_CODES.BAD_REQUEST:
      return STATUS_STRINGS.BAD_REQUEST;
    case STATUS_CODES.INTERNAL_ERROR:
      return STATUS_STRINGS.INTERNAL_ERROR;
    case STATUS_CODES.TIMEOUT:
      return STATUS_STRINGS.TIMEOUT;
  }
  return status;
};

export const isAdsEmptyInTraceRecord = (record) => {
  const adSpan = record.adsSpans.find((item) => item.adserver === record.adServer);
  if (!adSpan?.respBody) {
    return false;
  }

  try {
    const responseBody = JSON.parse(adSpan.respBody);
    return responseBody?.pods?.length === 1 && responseBody.pods[0]?.ads?.length === 0;
  } catch (error) {
    console.error('Failed to parse respBody:', error);
    return false;
  }
};

const traceTableStatusRender = (status, record) => {
  /* Display as pod miss if there are no ads, even if the status is 200 */
  if (status === '200' && (record.pod.includes('GET') || record.pod === 'PAUSE') && isAdsEmptyInTraceRecord(record)) {
    return <Tag color="orange">{STATUS_STRINGS.POD_MISS}</Tag>;
  }
  let tagColor = 'blue';
  if (status === '200' || status === '201') {
    tagColor = 'green';
  } else if (status === '204') {
    tagColor = 'orange';
  } else if (status.toString().startsWith('5') || status.toString().startsWith('4')) {
    tagColor = 'red';
  }

  return <Tag color={tagColor}>{translateStatus(status, record.pod)}</Tag>;
};

const createTableStatusFilters = (collection, attr) =>
  [...new Set(collection.map((item) => item[attr]))]
    .filter((item) => item)
    .map((uniqueItem) => ({ text: translateStatus(uniqueItem), value: uniqueItem }));

export const traceColumns = (traces, filters, widths, visibleCols, permissions = null) => {
  const zipkinUrlTemplate = envConfig.REACT_APP_ADS_TRACER_ZIPKIN_TRACE_URL_TEMPLATE || '';
  return [
    {
      title: TRACE_ID_COLUMN_TITLE,
      dataIndex: TRACE_ZIPKIN_TRACE_ID,
      key: TRACE_ZIPKIN_TRACE_ID,
      width: widths[TRACE_ZIPKIN_TRACE_ID] || 100,
      ellipsis: true,
      render: (_, record) => {
        const id = record[TRACE_ZIPKIN_TRACE_ID];
        if (!id) {
          return <span>{'\u2014'}</span>;
        }
        const canOpenZipkin = permissions && permissions.can(VIEW, ADS_TRACER_ZIPKIN_LINK) && zipkinUrlTemplate;
        const href = canOpenZipkin ? buildZipkinTraceUrl(zipkinUrlTemplate, id) : null;
        if (href) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              {id}
            </a>
          );
        }
        return <span>{id}</span>;
      },
    },
    {
      title: DEVICE_TITLE,
      dataIndex: TRACE_DEVICE_NAME,
      key: TRACE_DEVICE_NAME,
      width: widths[TRACE_DEVICE_NAME] || 100,
      filteredValue: filters[TRACE_DEVICE_NAME] || null,
      render: traceTableRowRender,
      filters: createTableColumnFilters(traces, TRACE_DEVICE_NAME),
      onFilter: (value, record) => record[TRACE_DEVICE_NAME] === value,
    },
    {
      title: INTEGRATION_TITLE,
      dataIndex: TRACE_INTEGRATION,
      key: TRACE_INTEGRATION,
      width: widths[TRACE_INTEGRATION] || 120,
      filteredValue: filters[TRACE_INTEGRATION] || null,
      render: traceTableRowRender,
      filters: createTableColumnFilters(traces, TRACE_INTEGRATION),
      onFilter: (value, record) => record[TRACE_INTEGRATION] === value,
    },
    {
      title: DEVICE_DISTRIBUTOR_TITLE,
      dataIndex: TRACE_DEVICE_DISTRIBUTOR_NAME,
      key: TRACE_DEVICE_DISTRIBUTOR_NAME,
      width: widths[TRACE_DEVICE_DISTRIBUTOR_NAME] || 120,
      filteredValue: filters[TRACE_DEVICE_DISTRIBUTOR_NAME] || null,
      render: traceTableRowRender,
      filters: createTableColumnFilters(traces, TRACE_DEVICE_DISTRIBUTOR_NAME),
      onFilter: (value, record) => record[TRACE_DEVICE_DISTRIBUTOR_NAME] === value,
    },
    {
      title: CONTENT_INFO_TITLE,
      dataIndex: 'contentInfo',
      key: TRACE_CONTENT_PARTNER_IDENTIFIER,
      width: widths[TRACE_CONTENT_PARTNER_IDENTIFIER] || 300,
      minWidth: 100,
      filteredValue: filters[TRACE_CONTENT_PARTNER_IDENTIFIER] || null,
      render: (text, record) => {
        // Prioritize name fields over identifier fields
        const displayContentPartner =
          record[TRACE_CONTENT_PARTNER_NAME] !== 'N/A'
            ? record[TRACE_CONTENT_PARTNER_NAME]
            : record[TRACE_CONTENT_PARTNER_IDENTIFIER];

        const displaySeries = record[TRACE_SERIES_NAME] !== 'N/A' ? record[TRACE_SERIES_NAME] : record[TRACE_SERIES_ID];

        return (
          <>
            <p>
              <b>Content Partner:</b> {displayContentPartner}
            </p>
            <p>
              <b>Series:</b> {displaySeries}
            </p>
            <p>
              <b>Title:</b> {record[TRACE_CONTENT_TITLE]}
            </p>
          </>
        );
      },
      filters: createTableColumnFilters(traces, TRACE_CONTENT_PARTNER_IDENTIFIER),
      onFilter: (value, record) =>
        record[TRACE_CONTENT_PARTNER_IDENTIFIER] === value || record[TRACE_CONTENT_PARTNER_NAME] === value,
    },
    {
      title: CONTENT_DISTRIBUTOR_TITLE,
      dataIndex: TRACE_CONTENT_DISTRIBUTOR_NAME,
      key: TRACE_CONTENT_DISTRIBUTOR_NAME,
      width: widths[TRACE_CONTENT_DISTRIBUTOR_NAME] || 120,
      filteredValue: filters[TRACE_CONTENT_DISTRIBUTOR_NAME] || null,
      ellipsis: true,
      render: traceTableRowRender,
      filters: createTableColumnFilters(traces, TRACE_CONTENT_DISTRIBUTOR_NAME),
      onFilter: (value, record) => record[TRACE_CONTENT_DISTRIBUTOR_NAME] === value,
    },
    {
      title: POD_TITLE,
      dataIndex: TRACE_POD,
      key: TRACE_POD,
      ellipsis: true,
      width: widths[TRACE_POD] || 80,
      filteredValue: filters[TRACE_POD] || null,
      render: traceTableRowRender,
      filters: createTableColumnFilters(traces, TRACE_POD),
      onFilter: (value, record) => record[TRACE_POD] === value,
    },
    {
      title: TIME_TITLE,
      dataIndex: TRACE_TIME,
      key: TRACE_TIME,
      width: widths[TRACE_TIME] || 175,
      ellipsis: true,
      render: traceTableRowRender,
      sorter: (t1, t2) => moment(t1['time']).unix() - moment(t2['time']).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: STATUS_TITLE,
      dataIndex: TRACE_STATUS,
      key: TRACE_STATUS,
      width: widths[TRACE_STATUS] || 120,
      ellipsis: true,
      filteredValue: filters[TRACE_STATUS] || null,
      render: (status, record) => traceTableStatusRender(status, record),
      filters: createTableStatusFilters(traces, TRACE_STATUS),
      onFilter: (value, record) => record[TRACE_STATUS] === value,
    },
    {
      title: COUNTRY_TITLE,
      dataIndex: TRACE_COUNTRY,
      key: TRACE_COUNTRY,
      width: widths[TRACE_COUNTRY] || 120,
      ellipsis: true,
      filteredValue: filters[TRACE_COUNTRY] || null,
      render: traceTableRowRender,
      filters: createTableStatusFilters(traces, TRACE_COUNTRY),
      onFilter: (value, record) => record[TRACE_COUNTRY] === value,
    },
    {
      title: AD_SERVER_TITLE,
      dataIndex: TRACE_AD_SERVER,
      key: TRACE_AD_SERVER,
      width: widths[TRACE_AD_SERVER] || 120,
      ellipsis: true,
      filteredValue: filters[TRACE_AD_SERVER] || null,
      render: traceTableRowRender,
      filters: createTableStatusFilters(traces, TRACE_AD_SERVER),
      onFilter: (value, record) => record[TRACE_AD_SERVER] === value,
    },
    {
      title: PUBLISHER_TITLE,
      dataIndex: TRACE_PUBLISHER,
      key: TRACE_PUBLISHER,
      width: widths[TRACE_PUBLISHER] || 120,
      ellipsis: true,
      filteredValue: filters[TRACE_PUBLISHER] || null,
      render: traceTableRowRender,
      filters: createTableStatusFilters(traces, TRACE_PUBLISHER),
      onFilter: (value, record) => record[TRACE_PUBLISHER] === value,
    },
  ].filter((col) => visibleCols.includes(col.title));
};

export const newTestCaseColumns = (
  showEmail,
  showOffPlatformFields,
  showAccountFields,
  showRokuFields,
  showFuboFields,
  showChannelField
) =>
  [
    {
      title: 'Profile Name',
      dataIndex: 'profileName',
      key: 'profileName',
    },
    {
      title: 'User Email',
      dataIndex: 'email',
      key: 'email',
      hidden: !showEmail,
    },
    {
      title: 'Profile Id',
      dataIndex: 'profileId',
      key: 'profileId',
      hidden: !showAccountFields,
    },
    {
      title: 'Account Id / Standard Web Id (SWID)',
      dataIndex: 'accountId',
      key: 'accountId',
      hidden: !showAccountFields,
    },
    {
      title: 'Device IFA',
      dataIndex: 'deviceIfa',
      key: 'deviceIfa',
      hidden: !showRokuFields,
    },
    {
      title: 'Fubo User ID',
      dataIndex: 'fuboUserId',
      key: 'fuboUserId',
      hidden: !showFuboFields,
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      hidden: !showOffPlatformFields,
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      hidden: !showChannelField,
    },
    {
      title: 'Distribution Partner',
      dataIndex: 'distroPartner',
      key: 'distroPartner',
      hidden: !showOffPlatformFields,
    },
  ].filter((item) => !item.hidden);

// Column keys for overrides table (commonColumns + overridesColumns)
const OVERRIDES_KEYS = {
  USER_ID: 'userId',
  PROFILE_ID: 'profileId',
  DEVICE_ID: 'deviceId',
  SWID: 'swid',
  FUBO_USER_ID: 'fuboUserId',
  VIEWER_ID: 'viewerId',
  AD_TYPE: 'adType',
  POD_POSITIONS: 'podPositions',
  EXPIRE_AT: 'expireAt',
  EDITOR_EMAIL: 'editorEmail',
  APP: 'app',
  EDIT_RULE: 'editRule',
  REMOVE_RULE: 'removeRule',
};

const commonColumns = (overrideBy, editRuleRender, removeRuleRender, viewEditCols, widths = {}) => {
  const isAdInvestigatorAppsSelectionEnabled =
    String(envConfig.REACT_APP_AD_INVESTIGATOR_APPS_SELECTION_ENABLED).toLowerCase() === 'true';
  const columns = [
    {
      title: 'Ad Type',
      dataIndex: OVERRIDES_KEYS.AD_TYPE,
      key: OVERRIDES_KEYS.AD_TYPE,
      width: widths[OVERRIDES_KEYS.AD_TYPE] || 100,
    },
    {
      title: 'Pod Positions',
      dataIndex: [OVERRIDES_KEYS.POD_POSITIONS],
      key: OVERRIDES_KEYS.POD_POSITIONS,
      width: widths[OVERRIDES_KEYS.POD_POSITIONS] || 250,
      render: (positions) =>
        Array.isArray(positions) ? (
          <Tree treeData={positions} className="selectable-tree" style={{ userSelect: 'text' }} />
        ) : (
          positions
        ),
    },
    {
      title: 'Expires At',
      dataIndex: OVERRIDES_KEYS.EXPIRE_AT,
      key: OVERRIDES_KEYS.EXPIRE_AT,
      width: widths[OVERRIDES_KEYS.EXPIRE_AT] || 100,
      render: (expireAt) => moment.unix(expireAt).format('MM/DD/YYYY H:mm:ss'),
    },
    {
      title: 'Last Editor',
      dataIndex: OVERRIDES_KEYS.EDITOR_EMAIL,
      key: OVERRIDES_KEYS.EDITOR_EMAIL,
      width: widths[OVERRIDES_KEYS.EDITOR_EMAIL] || 100,
    },
  ];

  if (isAdInvestigatorAppsSelectionEnabled && [DEVICE, SWID].includes(overrideBy)) {
    columns.unshift({
      title: 'App',
      dataIndex: OVERRIDES_KEYS.APP,
      key: OVERRIDES_KEYS.APP,
      width: widths[OVERRIDES_KEYS.APP] || 200,
      render: (app) => app || '-',
    });
  }

  if (viewEditCols) {
    columns.push(
      {
        title: 'Edit',
        key: OVERRIDES_KEYS.EDIT_RULE,
        width: widths[OVERRIDES_KEYS.EDIT_RULE] || 60,
        render: editRuleRender,
      },
      {
        title: 'Remove',
        key: OVERRIDES_KEYS.REMOVE_RULE,
        width: widths[OVERRIDES_KEYS.REMOVE_RULE] || 70,
        render: removeRuleRender,
      }
    );
  }

  return columns;
};

export const overridesColumns = (overrideBy, editRuleRender, removeRuleRender, viewEditCols, widths = {}) => {
  let specificColumns;

  if (overrideBy === USER_PROFILE) {
    specificColumns = [
      {
        title: 'User/Account Id',
        dataIndex: OVERRIDES_KEYS.USER_ID,
        key: OVERRIDES_KEYS.USER_ID,
        width: widths[OVERRIDES_KEYS.USER_ID] || 100,
      },
      {
        title: 'Profile Id',
        dataIndex: [OVERRIDES_KEYS.PROFILE_ID],
        key: OVERRIDES_KEYS.PROFILE_ID,
        width: widths[OVERRIDES_KEYS.PROFILE_ID] || 100,
      },
    ];
  } else if (overrideBy === DEVICE) {
    specificColumns = [
      {
        title: 'Device Id',
        dataIndex: OVERRIDES_KEYS.DEVICE_ID,
        key: OVERRIDES_KEYS.DEVICE_ID,
        width: widths[OVERRIDES_KEYS.DEVICE_ID] || 210,
      },
    ];
  } else if (overrideBy === SWID) {
    specificColumns = [
      {
        title: 'SWID',
        dataIndex: OVERRIDES_KEYS.SWID,
        key: OVERRIDES_KEYS.SWID,
        width: widths[OVERRIDES_KEYS.SWID] || 100,
      },
    ];
  } else if (overrideBy === FUBO_USER) {
    specificColumns = [
      {
        title: 'Fubo User Id',
        dataIndex: OVERRIDES_KEYS.FUBO_USER_ID,
        key: OVERRIDES_KEYS.FUBO_USER_ID,
        width: widths[OVERRIDES_KEYS.FUBO_USER_ID] || 140,
      },
    ];
  } else if (overrideBy === VIEWER_ID) {
    specificColumns = [
      {
        title: 'MLB Viewer Id',
        dataIndex: OVERRIDES_KEYS.VIEWER_ID,
        key: OVERRIDES_KEYS.VIEWER_ID,
        width: widths[OVERRIDES_KEYS.VIEWER_ID] || 140,
      },
    ];
  }

  return [...specificColumns, ...commonColumns(overrideBy, editRuleRender, removeRuleRender, viewEditCols, widths)];
};

// LIVEPOD DURATION MANAGER
export const durationColumns = (durationRules, widths) => [
  {
    title: 'App',
    dataIndex: DURATION_APP,
    key: DURATION_APP,
    filters: createTableColumnFilters(durationRules, DURATION_APP),
    onFilter: (value, record) => record[DURATION_APP] === value,
    width: widths[0] || 150,
    render: (app) => (app ? app.toUpperCase() : ''),
  },
  {
    title: 'Stream Types',
    dataIndex: STREAM_TYPE,
    key: STREAM_TYPE,
    filters: createTableColumnFilters(durationRules, STREAM_TYPE),
    onFilter: (value, record) => record[STREAM_TYPE] === value,
    width: widths[1] || 150,
  },
  {
    title: 'Rule Types',
    dataIndex: RULE_TYPE,
    key: RULE_TYPE,
    filters: createTableColumnFilters(durationRules, RULE_TYPE),
    onFilter: (value, record) => record[RULE_TYPE] === value,
    width: widths[2] || 150,
  },
  {
    title: 'IDs',
    dataIndex: [RULE_CHANNEL_ID, RULE_SERIES_ID, RULE_LEAGUE_ID, RULE_EVENT_ID, RULE_SPORTS_ID],
    key: IDS,
    render: idRender,
    width: widths[3] || 400,
  },
  {
    title: 'Duration(s)',
    dataIndex: RULE_DURATIONS_MILLISECONDS,
    key: RULE_DURATIONS_MILLISECONDS,
    width: widths[4] || 200,
  },
  {
    title: 'Partial Pod',
    dataIndex: RULE_PARTIAL_POD,
    key: RULE_PARTIAL_POD,
    filters: [
      {
        text: RULE_PARTIAL_POD_DISPLAY,
        value: RULE_PARTIAL_POD_DISPLAY,
      },
      {
        text: 'NOT ' + RULE_PARTIAL_POD_DISPLAY,
        value: '-',
      },
    ],
    onFilter: (value, record) => record[RULE_PARTIAL_POD] === value,
    width: widths[5] || 160,
  },
  {
    title: 'Single Program',
    dataIndex: RULE_SINGLE_PROGRAM,
    key: RULE_SINGLE_PROGRAM,
    filters: [
      {
        text: RULE_SINGLE_PROGRAM_DISPLAY,
        value: RULE_SINGLE_PROGRAM_DISPLAY,
      },
      {
        text: 'NOT ' + RULE_SINGLE_PROGRAM_DISPLAY,
        value: '-',
      },
    ],
    onFilter: (value, record) => record[RULE_SINGLE_PROGRAM] === value,
    width: widths[6] || 160,
  },
  {
    title: 'Back to Back Ads',
    dataIndex: RULE_BACKTOBACKADS,
    key: RULE_BACKTOBACKADS,
    filters: [
      {
        text: RULE_BACKTOBACKADS_ENABLED,
        value: true,
      },
    ],
    onFilter: (value, record) => record[RULE_BACKTOBACKADS] === value,
    width: widths[7] || 160,
    render: (val) => (val === true ? 'TRUE' : '-'),
  },
  {
    title: 'Status',
    dataIndex: RULE_STATUS,
    key: RULE_STATUS,
    render: ruleStatusRender,
    filters: createTableColumnFilters(durationRules, RULE_STATUS),
    onFilter: (value, record) => record[RULE_STATUS] === value,
    width: widths[8] || 100,
  },
  {
    title: 'Last Updated',
    dataIndex: RULE_LAST_UPDATED,
    key: RULE_LAST_UPDATED,
    width: widths[9] || 150,
    render: dateRender,
    defaultSortOrder: 'descend',
    sorter: (t1, t2) => moment(t1[RULE_LAST_UPDATED]).unix() - moment(t2[RULE_LAST_UPDATED]).unix(),
    showSorterTooltip: false,
  },
];

//Permission manager
export const permissionManagerColumns = (userData, widths) => [
  {
    title: 'User',
    dataIndex: USER_EMAIL,
    key: USER_EMAIL,
    width: widths[0] || 300,
  },
  {
    title: 'Roles',
    dataIndex: USER_ROLES,
    key: USER_ROLES,
    width: widths[1] || 600,
  },
];

// Q-CHECK
const qcTableVastPassRender = (pass) => (
  <Tag color={REQUIREMENT_VAST_PASS_STATUS[pass].color}>{REQUIREMENT_VAST_PASS_STATUS[pass].title}</Tag>
);
const qcTableMediaPassRender = (pass) => (
  <Tag color={REQUIREMENT_MEDIA_PASS_STATUS[pass].color}>{REQUIREMENT_MEDIA_PASS_STATUS[pass].title}</Tag>
);

const vastTableValidRender = (valid) => (
  <Tag color={TAG_VALID_STATUS[valid].color}>{TAG_VALID_STATUS[valid].title}</Tag>
);

const vastTablePrimaryRender = (isPrimary, record) => {
  const note = record[INGESTION_NOTE];
  if (!note) return null;
  if (isPrimary) {
    return (
      <Tooltip title={note}>
        <Tag color="blue">&#11088; PRIMARY</Tag>
      </Tooltip>
    );
  }
  if (note === NO_VALID_MEDIA_FILE) {
    return (
      <Tooltip title="No valid video asset exists in this ad for ingestion">
        <Tag color="orange">NONE</Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={note}>
      <Tag color="default">&mdash;</Tag>
    </Tooltip>
  );
};

// Config Manager
export const configDataCols = (data, widths) => [
  {
    title: 'Content Partner Name',
    dataIndex: CONTENT_PARTNER_NAME,
    key: CONTENT_PARTNER_NAME,
    width: widths[0] || 150,
  },
  {
    title: 'Source Id',
    dataIndex: SOURCE_ID,
    key: SOURCE_ID,
    width: widths[1] || 150,
  },
  {
    title: 'Trigger',
    dataIndex: TRIGGER_TYPE,
    key: TRIGGER_TYPE,
    width: widths[2] || 150,
    filters: createTableColumnFilters(data, TRIGGER_TYPE),
    onFilter: (value, record) => record[TRIGGER_TYPE] === value,
  },
  {
    title: 'UPID Lookup Strategy',
    dataIndex: UPID_LOOKUP_STRATEGY,
    key: UPID_LOOKUP_STRATEGY,
    width: widths[3] || 150,
    filters: createTableColumnFilters(data, UPID_LOOKUP_STRATEGY),
    onFilter: (value, record) => record[UPID_LOOKUP_STRATEGY] === value,
  },
  {
    title: 'Pod Fetch Method',
    dataIndex: POD_FETCH_METHOD,
    key: POD_FETCH_METHOD,
    width: widths[4] || 150,
    filters: createTableColumnFilters(data, POD_FETCH_METHOD),
    onFilter: (value, record) => record[POD_FETCH_METHOD] === value,
  },
  {
    title: 'Status',
    dataIndex: STATUS,
    key: STATUS,
    width: widths[5] || 100,
    render: configStatusRender,
    filters: createTableColumnFilters(data, STATUS),
    onFilter: (value, record) => record[STATUS] === value,
  },
  {
    title: 'Updated By',
    dataIndex: UPDATED_BY,
    key: UPDATED_BY,
    width: widths[6] || 150,
    filters: createTableColumnFilters(data, UPDATED_BY),
    onFilter: (value, record) => record[UPDATED_BY] === value,
  },
  {
    title: 'Updated At',
    dataIndex: UPDATED_AT,
    key: UPDATED_AT,
    width: widths[6] || 150,
    defaultSortOrder: 'descend',
    sorter: (t1, t2) => moment(t1[UPDATED_AT]).unix() - moment(t2[UPDATED_AT]).unix(),
  },
];

export const configMgrHistoryCols = (data) => [
  {
    title: 'Content Partner Name',
    dataIndex: CONTENT_PARTNER_NAME,
    key: CONTENT_PARTNER_NAME,
    width: 50,
    filters: createTableColumnFilters(data, CONTENT_PARTNER_NAME),
    onFilter: (value, record) => record[CONTENT_PARTNER_NAME] === value,
  },
  {
    title: 'Source Id',
    dataIndex: SOURCE_ID,
    key: SOURCE_ID,
    width: 100,
  },
  {
    title: 'Trigger',
    dataIndex: TRIGGER_TYPE,
    key: TRIGGER_TYPE,
    width: 50,
    filters: createTableColumnFilters(data, TRIGGER_TYPE),
    onFilter: (value, record) => record[TRIGGER_TYPE] === value,
  },
  {
    title: 'UPID Lookup Strategy',
    dataIndex: UPID_LOOKUP_STRATEGY.name,
    key: UPID_LOOKUP_STRATEGY,
    width: 50,
    render: (item) => item[UPID_LOOKUP_STRATEGY],
    filters: createTableColumnFilters(data, UPID_LOOKUP_STRATEGY),
    onFilter: (value, record) => record[UPID_LOOKUP_STRATEGY] === value,
  },
  {
    title: 'Pod Fetch Method',
    dataIndex: POD_FETCH_METHOD,
    key: POD_FETCH_METHOD,
    width: 50,
    filters: createTableColumnFilters(data, POD_FETCH_METHOD),
    onFilter: (value, record) => record[POD_FETCH_METHOD] === value,
  },
  {
    title: 'Network',
    dataIndex: UPID_LOOKUP_STRATEGY[NETWORK_NAME],
    key: NETWORK_NAME,
    width: 50,
    render: (item) => item[NETWORK_NAME],
    filters: createTableColumnFilters(data, NETWORK_NAME),
    onFilter: (value, record) => record[NETWORK_NAME] === value,
  },
  {
    title: 'Status',
    dataIndex: STATUS,
    key: STATUS,
    width: 50,
    render: configStatusRender,
    filters: createTableColumnFilters(data, STATUS),
    onFilter: (value, record) => record[STATUS] === value,
  },
  {
    title: 'Action',
    dataIndex: HISTORY_ACTION,
    key: HISTORY_ACTION,
    width: 50,
    filters: createTableColumnFilters(data, HISTORY_ACTION),
    onFilter: (value, record) => record[HISTORY_ACTION] === value,
  },
  {
    title: 'Updated By',
    dataIndex: UPDATED_BY,
    key: UPDATED_BY,
    width: 50,
    filters: createTableColumnFilters(data, UPDATED_BY),
    onFilter: (value, record) => record[UPDATED_BY] === value,
  },
  {
    title: 'Updated At',
    dataIndex: UPDATED_AT,
    key: UPDATED_AT,
    width: 50,
    render: dateRender,
    defaultSortOrder: 'descend',
    sorter: (t1, t2) => moment(t1[UPDATED_AT]).unix() - moment(t2[UPDATED_AT]).unix(),
  },
];

export const mediaRequirementColumns = () => [
  {
    title: 'Pass',
    dataIndex: MEDIA_PASS_STATUS,
    key: MEDIA_PASS_STATUS,
    width: 65,
    render: qcTableMediaPassRender,
  },
  {
    title: 'Parameter',
    dataIndex: PARAMETER,
    key: PARAMETER,
    sorter: (p1, p2) => p1[PARAMETER].localeCompare(p2[PARAMETER]),
    width: 155,
  },
  {
    title: 'Value',
    dataIndex: VALUE,
    key: VALUE,
    width: 150,
  },
  {
    title: 'Type',
    dataIndex: TYPE,
    key: TYPE,
    sorter: (t1, t2) => t1[TYPE].localeCompare(t2[TYPE]),
    width: 100,
  },
  {
    title: 'Error Message',
    dataIndex: ERROR_MESSAGE,
    key: ERROR_MESSAGE,
    width: 150,
    render: (text, record) => (record['status'] === 'violation' ? text : null),
  },
];

const getMediaFileRequirementValue = (record, paramName, streamType) => {
  const requirements = record['media-file-validation']?.['media-file-requirements'];
  if (!Array.isArray(requirements)) return null;
  const req = requirements.find((r) => r['stream-parameter'] === paramName && r['stream-type'] === streamType);
  return req?.['stream-parameter-value'] ?? null;
};

export const vastRequirementColumns = () => [
  {
    title: 'Pass',
    dataIndex: VAST_PASS,
    key: VAST_PASS,
    width: 50,
    render: qcTableVastPassRender,
  },
  {
    title: 'Video Bit Rate (kbps)',
    key: VIDEO_BIT_RATE,
    width: 150,
    render: (_, record) => getMediaFileRequirementValue(record, 'bit_rate_kbps', 'VIDEO'),
  },
  {
    title: 'Video Resolution',
    key: VIDEO_RESOLUTION,
    width: 130,
    render: (_, record) => getMediaFileRequirementValue(record, 'resolution', 'VIDEO'),
  },
  {
    title: 'Vast',
    dataIndex: VAST,
    key: VAST,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Ad',
    dataIndex: AD,
    key: AD,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Inline',
    dataIndex: INLINE,
    key: INLINE,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Impression',
    dataIndex: IMPRESSION,
    key: IMPRESSION,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Creative',
    dataIndex: CREATIVE,
    key: CREATIVE,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Linear',
    dataIndex: LINEAR,
    key: LINEAR,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Media File',
    dataIndex: MEDIA_FILE,
    key: MEDIA_FILE,
    width: 100,
    render: vastTableValidRender,
  },
  {
    title: 'Ad ID',
    dataIndex: AD_ID,
    key: AD_ID,
    width: 150,
  },
  {
    title: 'Primary',
    dataIndex: PRIMARY,
    key: PRIMARY,
    width: 100,
    render: vastTablePrimaryRender,
  },
];
export const creativeTrackerExportFieldsV2 = {
  [ASSET_ID_KEY]: 'Asset Id',
  [SOURCE_AD_ID_KEY]: {
    header: 'Source Ad Id',
    formatter: (value, record) => {
      return record[ASSET_SOURCES_KEY]?.map((source) => source[SOURCE_AD_ID_KEY])[0] ?? null;
    },
  },
  [SOURCE_CREATIVE_ID_KEY]: {
    header: 'Source Creative Id',
    formatter: (value, record) => {
      return record[ASSET_SOURCES_KEY]?.map((source) => source[SOURCE_CREATIVE_ID_KEY])[0] ?? null;
    },
  },
  [ASSET_NAME_KEY]: 'Asset Name',
  [REVIEW_STATUS_KEY]: 'Status',
  [BRAND_NAME_KEY]: {
    header: 'Brand Name',
    formatter: (value, record) =>
      formatArrayToMultipleLines([
        ...new Set(record.brand?.map((brand) => brand[BRAND_NAME_KEY]).filter((name) => name !== undefined)),
      ]),
  },
  [ADOMAIN_KEY]: {
    header: 'Adomain',
    formatter: (value, record) => record[ASSET_SOURCES_KEY]?.map((source) => source[ADOMAIN_KEY])[0] ?? null,
  },
  [CREATED_AT_KEY]: {
    header: 'Created At',
    formatter: (fieldValue) => moment(fieldValue).local().format('YYYY-MM-DD HH:mm:ss'), // Convert UTC to local time
  },
  [UPDATED_AT_KEY]: {
    header: 'Updated At',
    formatter: (fieldValue) => moment(fieldValue).local().format('YYYY-MM-DD HH:mm:ss'), // Convert UTC to local time
  },
};

const formatArrayToMultipleLines = (val) => {
  if (!Array.isArray(val)) return ''; // Safeguard for null or non-array values
  return val
    .filter((v) => v !== null)
    .map((v) => v.replace(/ /g, '\u00A0')) // Replace single spaces
    .join('\u000A');
};

export const creativesColumnsV2 = (
  creatives,
  widths,
  statusItemRenderer,
  permissions,
  missionControlBaseUrl,
  handleMediaPreviewClick
) => {
  return [
    {
      title: 'Asset ID',
      dataIndex: ASSET_ID_KEY,
      key: ASSET_ID_KEY,
      filters: createTableColumnFilters(creatives, ASSET_ID_KEY),
      onFilter: (value, record) => record[ASSET_ID_KEY] === value,
      align: 'center',
      render: (assetId) => {
        return permissions.can(VIEW, ASSET_REVIEWER_LINK) ? (
          <a
            href={`${missionControlBaseUrl}/ads-asset2-reviewer-ui/asset/${assetId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // Prevents drawer from opening
          >
            {assetId}
          </a>
        ) : (
          assetId
        );
      },
      width: widths[ASSET_ID_KEY] || 100,
    },
    {
      title: 'Source Ad ID',
      dataIndex: SOURCE_AD_ID_KEY,
      key: SOURCE_AD_ID_KEY,
      render: (value, record) => {
        return record[ASSET_SOURCES_KEY]?.map((source) => source[SOURCE_AD_ID_KEY])[0] ?? null;
      },
      ellipsis: false,
      filters: createTableColumnFilters(creatives, SOURCE_AD_ID_KEY),
      onFilter: (value, record) => record[SOURCE_AD_ID_KEY] === value,
      width: widths[SOURCE_AD_ID_KEY] || 50,
    },
    {
      title: 'Source Creative ID',
      dataIndex: SOURCE_CREATIVE_ID_KEY,
      key: SOURCE_CREATIVE_ID_KEY,
      render: (value, record) => {
        return record[ASSET_SOURCES_KEY]?.map((source) => source[SOURCE_CREATIVE_ID_KEY])[0] ?? null;
      },
      ellipsis: false,
      filters: createTableColumnFilters(creatives, SOURCE_CREATIVE_ID_KEY),
      onFilter: (value, record) => record[SOURCE_CREATIVE_ID_KEY] === value,
      width: widths[SOURCE_CREATIVE_ID_KEY] || 50,
    },
    {
      title: 'Asset Name',
      dataIndex: ASSET_NAME_KEY,
      key: ASSET_NAME_KEY,
      ellipsis: true,
      filters: createTableColumnFilters(creatives, ASSET_NAME_KEY),
      onFilter: (value, record) => record[ASSET_NAME_KEY] === value,
      width: widths[ASSET_NAME_KEY] || 100,
    },
    {
      title: 'Status',
      dataIndex: REVIEW_STATUS_KEY,
      key: REVIEW_STATUS_KEY,
      ellipsis: false,
      render: statusItemRenderer,
      filters: createTableColumnFilters(creatives, REVIEW_STATUS_KEY),
      onFilter: (value, record) => record[REVIEW_STATUS_KEY] === value,
      width: widths[REVIEW_STATUS_KEY] || 50,
    },
    {
      dataIndex: TRANSCODE_QC_PLAYLIST_KEY,
      key: TRANSCODE_QC_PLAYLIST_KEY,
      title: 'Preview',
      render: (value, record) => {
        const transcodePlaylist = record[TRANSCODE_QC_PLAYLIST_KEY];
        if (transcodePlaylist) {
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleMediaPreviewClick(transcodePlaylist);
              }}
            >
              <PlayCircleTwoTone style={{ fontSize: '30px' }} twoToneColor={'purple'} />
            </div>
          );
        } else {
          return <PlayCircleTwoTone style={{ fontSize: '30px' }} twoToneColor={'gray'} />;
        }
      },
      width: widths[PREVIEW_KEY] || 50,
    },
    {
      title: 'Brand Name',
      dataIndex: BRAND_NAME_KEY,
      key: BRAND_NAME_KEY,
      render: (value, record) => {
        return [...new Set(record.brand?.map((brand) => brand[BRAND_NAME_KEY]))].filter(Boolean).join(', ') || '';
      },
      filters: createTableFiltersFromSubObjects(creatives, BRAND_KEY, BRAND_NAME_KEY),
      onFilter: (value, record) => record[BRAND_KEY]?.some((brand) => brand[BRAND_NAME_KEY] === value),
      width: widths[BRAND_NAME_KEY] || 100,
    },
    {
      title: 'Adomain',
      dataIndex: ADOMAIN_KEY,
      key: ADOMAIN_KEY,
      render: (value, record) => {
        return record[ASSET_SOURCES_KEY]?.map((source) => source[ADOMAIN_KEY])[0] ?? null;
      },
      filters: createTableFiltersFromSubObjects(creatives, ASSET_SOURCES_KEY, ADOMAIN_KEY),
      onFilter: (value, record) => record[ASSET_SOURCES_KEY]?.some((source) => source[ADOMAIN_KEY] === value),
      width: widths[ADOMAIN_KEY] || 100,
    },
    {
      title: 'Created At',
      dataIndex: CREATED_AT_KEY,
      key: CREATED_AT_KEY,
      render: (createAt) => moment.utc(createAt).local().format('MM/DD/YYYY HH:mm:ss'),
      sorter: (a, b) => moment(a[CREATED_AT_KEY]).valueOf() - moment(b[CREATED_AT_KEY]).valueOf(),
      width: widths[CREATED_AT_KEY] || 100,
    },
    {
      title: 'Updated At',
      dataIndex: UPDATED_AT_KEY,
      key: UPDATED_AT_KEY,
      render: (updateAt) => moment.utc(updateAt).local().format('MM/DD/YYYY HH:mm:ss'),
      sorter: (a, b) => moment(a[UPDATED_AT_KEY]).valueOf() - moment(b[UPDATED_AT_KEY]).valueOf(),
      defaultSortOrder: 'descend',
      width: widths[UPDATED_AT_KEY] || 100,
    },
  ];
};

export const bulkUploaddurationRulesColumns = (dataSource, widths) => [
  {
    title: 'App',
    dataIndex: DURATION_APP,
    key: DURATION_APP,
    filters: createTableColumnFilters(dataSource, DURATION_APP),
    onFilter: (value, record) => record[DURATION_APP] === value,
    width: widths[0] || 150,
    render: (app, record) =>
      record[STREAM_TYPE] === 'LINEAR' && record[RULE_TYPE] === 'CHANNEL' ? app?.toUpperCase() || '' : '',
  },
  {
    title: 'Stream Type',
    dataIndex: STREAM_TYPE,
    key: STREAM_TYPE,
    filters: createTableColumnFilters(dataSource, RULE_TYPE),
    onFilter: (value, record) => record[RULE_TYPE] === value,
    width: widths[1] || 150,
  },
  {
    title: 'Rule Type',
    dataIndex: RULE_TYPE,
    key: RULE_TYPE,
    filters: createTableColumnFilters(dataSource, RULE_TYPE),
    onFilter: (value, record) => record[RULE_TYPE] === value,
    width: widths[2] || 150,
  },
  {
    title: 'Channel Id',
    dataIndex: CHANNEL_ID,
    key: CHANNEL_ID,
    filters: createTableColumnFilters(dataSource, CHANNEL_ID),
    onFilter: (value, record) => record[CHANNEL_ID] === value,
    width: widths[3] || 300,
  },
  {
    title: 'Series Id',
    dataIndex: SERIES_ID,
    key: SERIES_ID,
    filters: createTableColumnFilters(dataSource, SERIES_ID),
    onFilter: (value, record) => record[SERIES_ID] === value,
    width: widths[4] || 300,
  },
  {
    title: 'League Id',
    dataIndex: LEAGUE_ID,
    key: LEAGUE_ID,
    filters: createTableColumnFilters(dataSource, LEAGUE_ID),
    onFilter: (value, record) => record[LEAGUE_ID] === value,
    width: widths[5] || 300,
  },
  {
    title: 'Event Id',
    dataIndex: EVENT_ID,
    key: EVENT_ID,
    filters: createTableColumnFilters(dataSource, EVENT_ID),
    onFilter: (value, record) => record[EVENT_ID] === value,
    width: widths[6] || 300,
  },
  {
    title: 'Sports Id',
    dataIndex: SPORTS_ID,
    key: SPORTS_ID,
    filters: createTableColumnFilters(dataSource, SPORTS_ID),
    onFilter: (value, record) => record[SPORTS_ID] === value,
    width: widths[7] || 300,
  },
  {
    title: 'Pod Durations (ms)',
    dataIndex: DURATION_MS,
    key: DURATION_MS,
    render: (durations) => durations.map((duration) => <Tag key={duration}>{duration}</Tag>),
    width: widths[8] || 150,
  },
  {
    title: (
      <Popover
        trigger="hover"
        placement="right"
        overlayStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '300px' }}
        title={<h3>Usage</h3>}
        content={'This field is exclusively applicable in CHANNEL rules. For any other rules, it will be disregarded.'}
      >
        <span>Single Program </span>
        <InfoCircleOutlined />
      </Popover>
    ),
    dataIndex: SINGLE_PROGRAM,
    key: SINGLE_PROGRAM,
    render: (val) => (val === undefined ? '' : val.toString()),
    width: widths[9] || 100,
  },
  {
    title: 'Partial Pod',
    dataIndex: PARTIAL_POD,
    key: PARTIAL_POD,
    render: (val) => (val === undefined ? '' : val.toString()),
    width: widths[10] || 100,
  },
  {
    title: 'Back to Back Ads',
    dataIndex: BACK_TO_BACK_ADS,
    key: BACK_TO_BACK_ADS,
    width: widths[11] || 120,
    render: (val) => (val === undefined ? '' : val.toString()),
  },
  {
    title: 'Change Type',
    dataIndex: CHANGE_TYPE,
    key: CHANGE_TYPE,
    filters: createTableColumnFilters(dataSource, CHANGE_TYPE),
    width: widths[12] || 100,
    onFilter: (value, record) => record[CHANGE_TYPE] === value,
  },
];

export const reservationAuditsColumns = (dataSource, widths) => [
  {
    title: 'Last Updated',
    dataIndex: 'lastUpdated',
    key: 'lastUpdated',
    sorter: (a, b) => moment(a.lastUpdated).unix() - moment(b.lastUpdated).unix(),
    defaultSortOrder: 'descend',
    render: dateRender,
    width: widths[0] || 100,
  },
  {
    title: 'User Name',
    dataIndex: 'username',
    key: 'username',
    filters: createTableColumnFilters(dataSource, 'username'),
    onFilter: (value, record) => String(record.username) === value,
    width: widths[1] || 100,
  },
  {
    title: 'Content Partner',
    dataIndex: 'contentPartnerName',
    key: 'contentPartnerName',
    filters: createTableColumnFilters(dataSource, 'contentPartnerName'),
    onFilter: (value, record) => String(record.contentPartnerName) === value,
    width: widths[2] || 100,
  },
  {
    title: 'Field',
    dataIndex: 'field',
    key: 'field',
    filters: createTableColumnFilters(dataSource, 'field'),
    onFilter: (value, record) => String(record.field) === value,
    width: widths[3] || 100,
  },
  {
    title: 'Old Value',
    dataIndex: 'oldValue',
    key: 'oldValue',
    width: widths[4] || 100,
  },
  {
    title: 'New Value',
    dataIndex: 'newValue',
    key: 'newValue',
    width: widths[5] || 100,
  },
  {
    title: 'Date Created',
    dataIndex: 'dateCreated',
    key: 'dateCreated',
    sorter: (a, b) => moment(a.dateCreated).unix() - moment(b.dateCreated).unix(),
    render: dateRender,
    width: widths[6] || 100,
  },
];

// AUCTION SNAPSHOT
export const adRequestSnapshotColumns = (adRequestSnapshotActions, sortOrder) => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'snapshotName',
    width: '27%',
    sorter: true,
    sortDirections: ['ascend', 'descend', 'ascend'],
    sortOrder: sortOrder['snapshotName'],
    ellipsis: true,
    render: (text, record) => editableSnapshotNameCell(text, record, adRequestSnapshotActions),
  },
  {
    title: 'Date',
    sorter: true,
    sortDirections: ['descend', 'ascend', 'descend'],
    sortOrder: sortOrder['date'],
    dataIndex: 'date',
    key: 'date',
    width: '20%',
    render: formatDate,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: '10%',
    render: (text) => snapshotStatuses(text),
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: '13%',
    render: (data) => data && `${data.firstName} ${data.secondName}`,
  },
  {
    title: 'Returned Auctions',
    dataIndex: 'returnedAuctions',
    key: 'returnedAuctions',
    width: '11%',
  },
  {
    title: '',
    dataIndex: 'controlBtn',
    key: 'controlBtn',
    width: '15%',
    render: (text, record) => auctionSnapshotActions(adRequestSnapshotActions, record),
  },
];

export const auctionDetailsColumns = [
  {
    title: '',
    key: 'index',
    render: (text, record, index) => index + 1,
  },
  {
    title: 'Name',
    dataIndex: 'externalAuctionId',
    key: 'externalAuctionId',
    width: '27%',
    render: (text, record) => (
      <StyledAuctionName
        href={`${AUCTION_SNAPSHOT_DETAILED_VIEW}/${record.id}`}
        title="External Auction ID"
        rel="noreferrer"
      >
        {text}
      </StyledAuctionName>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: '22%',
    render: formatDate,
  },
  {
    title: 'Attempted',
    dataIndex: 'attempted',
    key: 'attempted',
    width: '13%',
  },
  {
    title: 'Returned',
    dataIndex: 'returned',
    key: 'returned',
    width: '12%',
  },
  {
    title: 'App Name',
    dataIndex: 'appName',
    key: 'appName',
    width: '7%',
  },
  {
    title: 'Device Name',
    dataIndex: 'deviceFamily',
    key: 'deviceFamily',
    width: '8%',
  },
];

export const vastTrackerColumns = (columns, widths) =>
  columns.map((column, index) => ({ ...column, width: index === 0 ? widths[0] || 300 : widths[index] || 125 }));

// Utility sorters for Request Tracer columns
const stringSorter = (a, b, key) =>
  String(a[key] || '').localeCompare(String(b[key] || ''), undefined, { sensitivity: 'base' });
const boolSorter = (a, b, key) => (a[key] === b[key] ? 0 : a[key] ? -1 : 1);

export const requestTracerSlotQualificationColumns = (dataSource, widths = {}) => [
  {
    title: 'Endpoint',
    dataIndex: SLOT_QUALIFICATION_ENDPOINT,
    key: SLOT_QUALIFICATION_ENDPOINT,
    width: widths[SLOT_QUALIFICATION_ENDPOINT] || 150,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_ENDPOINT),
  },
  {
    title: 'Invariant Qualified',
    dataIndex: SLOT_QUALIFICATION_INVARIANT_QUALIFIED,
    key: SLOT_QUALIFICATION_INVARIANT_QUALIFIED,
    width: widths[SLOT_QUALIFICATION_INVARIANT_QUALIFIED] || 100,
    render: (value) => <BooleanBubble value={value} />,
    sorter: (a, b) => boolSorter(a, b, SLOT_QUALIFICATION_INVARIANT_QUALIFIED),
  },
  {
    title: 'Slot Qualified',
    dataIndex: SLOT_QUALIFICATION_SLOT_QUALIFIED,
    key: SLOT_QUALIFICATION_SLOT_QUALIFIED,
    width: widths[SLOT_QUALIFICATION_SLOT_QUALIFIED] || 100,
    render: (value) => <BooleanBubble value={value} />,
    sorter: (a, b) => boolSorter(a, b, SLOT_QUALIFICATION_SLOT_QUALIFIED),
  },
  {
    title: 'DQ Status',
    dataIndex: SLOT_QUALIFICATION_DQ_STATUS,
    key: SLOT_QUALIFICATION_DQ_STATUS,
    width: widths[SLOT_QUALIFICATION_DQ_STATUS] || 150,
    render: (value) => (
      <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{getDQStatusBubble(value)}</div>
    ),
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_DQ_STATUS),
  },
  {
    title: 'Selection',
    dataIndex: SLOT_QUALIFICATION_SELECTION,
    key: SLOT_QUALIFICATION_SELECTION,
    width: widths[SLOT_QUALIFICATION_SELECTION] || 100,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_SELECTION),
  },
  {
    title: 'Pod Position',
    dataIndex: SLOT_QUALIFICATION_POD_POSITION,
    key: SLOT_QUALIFICATION_POD_POSITION,
    width: widths[SLOT_QUALIFICATION_POD_POSITION] || 100,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_POD_POSITION),
  },
  {
    title: 'Slot Position',
    dataIndex: SLOT_QUALIFICATION_SLOT_POSITION,
    key: SLOT_QUALIFICATION_SLOT_POSITION,
    width: widths[SLOT_QUALIFICATION_SLOT_POSITION] || 100,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_SLOT_POSITION),
  },
  {
    title: 'Qualified Ads',
    dataIndex: SLOT_QUALIFICATION_QUALIFIED_ADS,
    key: SLOT_QUALIFICATION_QUALIFIED_ADS,
    width: widths[SLOT_QUALIFICATION_QUALIFIED_ADS] || 100,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_QUALIFIED_ADS),
  },
  {
    title: 'Filler Only',
    dataIndex: SLOT_QUALIFICATION_FILLER_ONLY,
    key: SLOT_QUALIFICATION_FILLER_ONLY,
    width: widths[SLOT_QUALIFICATION_FILLER_ONLY] || 100,
    render: (value) => <BooleanBubble value={value} />,
    sorter: (a, b) => boolSorter(a, b, SLOT_QUALIFICATION_FILLER_ONLY),
  },
  {
    title: 'DQ Reason',
    dataIndex: SLOT_QUALIFICATION_DQ_REASON,
    key: SLOT_QUALIFICATION_DQ_REASON,
    width: widths[SLOT_QUALIFICATION_DQ_REASON] || 100,
    sorter: (a, b) => stringSorter(a, b, SLOT_QUALIFICATION_DQ_REASON),
  },
  {
    title: 'In Next Sequence',
    dataIndex: SLOT_QUALIFICATION_IN_NEXT_SEQUENCE,
    key: SLOT_QUALIFICATION_IN_NEXT_SEQUENCE,
    width: widths[SLOT_QUALIFICATION_IN_NEXT_SEQUENCE] || 100,
    render: (value) => <BooleanBubble value={value} />,
    sorter: (a, b) => boolSorter(a, b, SLOT_QUALIFICATION_IN_NEXT_SEQUENCE),
  },
];

export const requestTracerInvariantQualificationColumns = (dataSource, widths = {}) => [
  {
    title: 'Endpoint',
    dataIndex: INVARIANT_QUALIFICATION_ENDPOINT,
    key: INVARIANT_QUALIFICATION_ENDPOINT,
    width: widths[INVARIANT_QUALIFICATION_ENDPOINT] || 100,
    sorter: (a, b) => stringSorter(a, b, INVARIANT_QUALIFICATION_ENDPOINT),
  },
  {
    title: 'Qualified Ads',
    dataIndex: INVARIANT_QUALIFICATION_QUALIFIED_ADS,
    key: INVARIANT_QUALIFICATION_QUALIFIED_ADS,
    width: widths[INVARIANT_QUALIFICATION_QUALIFIED_ADS] || 100,
    sorter: (a, b) => stringSorter(a, b, INVARIANT_QUALIFICATION_QUALIFIED_ADS),
  },
  {
    title: 'DQ Status',
    dataIndex: INVARIANT_QUALIFICATION_DQ_STATUS,
    key: INVARIANT_QUALIFICATION_DQ_STATUS,
    width: widths[INVARIANT_QUALIFICATION_DQ_STATUS] || 100,
    render: getDQStatusBubble,
    sorter: (a, b) => stringSorter(a, b, INVARIANT_QUALIFICATION_DQ_STATUS),
  },
  {
    title: 'DQ Reason',
    dataIndex: INVARIANT_QUALIFICATION_DQ_REASON,
    key: INVARIANT_QUALIFICATION_DQ_REASON,
    width: widths[INVARIANT_QUALIFICATION_DQ_REASON] || 100,
    sorter: (a, b) => stringSorter(a, b, INVARIANT_QUALIFICATION_DQ_REASON),
  },
];
